import store from "@app/store.js";
import { notify } from "@app/notification/notification.action.js";
import * as userActions from "@app/user/user.action.js";
import { fetchWithRetry, Fetcher } from "./fetcher.js";
import apiUrl, { customUrl } from "./url.js";
import { tr } from "./translation.js";

const fetchPublishers = (callbackFunc, userid) => {
  // Initialize available publishers
  return fetchWithRetry("getJson", [apiUrl("publishers", false)])
    .then(async (data) => {
      const publishers = {};
      for (const item of data) {
        // Build 4.1.2.0(ERP #12346)...
        if (userid !== null && userid !== "") {
          console.log({userid, item})
          const valid = await checkUserValidToPublisher(userid, item.api_prefix);
          if (valid) {
            publishers[item.name] = item.api_prefix;
          }
        } else {
          publishers[item.name] = item.api_prefix;
        }
        // ... Build 4.1.2.0(ERP #12346)
      }

      // Build 4.1.2.0(ERP #12346)
      if (
        userid !== null &&
        userid !== "" &&
        data !== null &&
        data.length > 0 &&
        (publishers === null || publishers.length <= 0)
      ) {
        callbackFunc(null, null);
        return;
      }

      // Set the first publisher to being currently selected
      const current = data[0].name;

      getUserData("publisher")
        .then((response) => {
          if (response === null) {
            callbackFunc(publishers, current);
          } else {
            callbackFunc(publishers, response);
          }
        })
        .catch((error) => {
          throw error;
        });
    })
    .catch(() => {
      store.dispatch(notify(tr("dbConnectFailure"), "error"));
      callbackFunc(null, null);
    });
};

export const initPublishers = (callbackFunc, userid) => {
  // Build 4.1.2.0(ERP #12346): add userid
/*  publishers.forEach(function (element) {
    console.log("Publishers " + element);
  });
  console.log("Current " + current);*/
  fetchPublishers((publishers, current) => {
    if (publishers === null && current === null) {
      store.dispatch(notify(tr("publisherFetchFailed"), "error"));
      callbackFunc(false);
    } else {
      document.title = current;
      store.dispatch(userActions.setPublishers(publishers, current));
      callbackFunc(true);
      getPaperCodes();
    }
  }, userid);
};

export const getPaperCodes = (callbackFunc = () => {}) => {
  const url = apiUrl("subscriptions/papers");

  fetchWithRetry("getJson", [url])
    .then((response) => {
      store.dispatch(userActions.setPaperCodes(response));
      callbackFunc();
    })
    .catch(() => {
      store.dispatch(notify(tr("dbConnectFailure"), "error"));
      callbackFunc();
    });
};

export const userReady = () => {
  const user = store.getState().user;
  const publishers = user.publishers;

  const propertiesReady = user && user.properties && Object.keys(user.properties).length > 0;
  const publishersReady =
  user && user.properties && Object.keys(publishers.publishers).length > 0 && publishers.current !== "";

  return propertiesReady && publishersReady;
};

/*
 * User properties include information which is necessary for the application.
 * This includes name, id, and email information.
 */
export const getUserProperties = (callbackFunc) => {
  const url = apiUrl("users/properties", false);

  fetchWithRetry("getJson", [url])
    .then((response) => {
      let properties = {};
      let forceResetPassword = false;
      if (response !== null) {
        properties = {
          username: response.username,
          userid: response.userid,
          email: response.emailaddress,
        };

        forceResetPassword = response.chgpwd_nextlogin && response.chgpwd_nextlogin === "Y";
      }
      store.dispatch(userActions.setUserInfo(properties));
      store.dispatch(userActions.setForceResetPassword(forceResetPassword));
      callbackFunc();
    })
    .catch(() => {
      store.dispatch(notify(tr("dbConnectFailure"), "error"));
      callbackFunc();
    });
};

/*
 * User properties include information which not mandatory for the application.
 * This includes table column visibilities.
 */
export const getUserSettings = (callbackFunc = null) => {
  return getUserData("settings")
    .then((data) => {
      if (data !== null) {
        try {
          const parsed = JSON.parse(data);
          store.dispatch(userActions.setUserSettings(parsed));
        } catch (error) {
          store.dispatch(notify(tr("userSettingsInvalid"), "warning"));
        }
      }
      if (callbackFunc !== null) {
        callbackFunc();
      }
    })
    .catch(() => {
      store.dispatch(notify(tr("dbConnectFailure"), "error"));

      if (callbackFunc !== null) {
        callbackFunc();
      }
    });
};

/*
 * Save user's settings into Kayak UsersAPI under the key 'settings'.
 *
 * Parameter 'newState' should be an object containing desired new values.
 * The given object will be merged with the existing user settings.
 *
 * Example:
 *  old settings: { table: { columns: { feedback: [6] } } }
 *  newState:     { table: { columns: { subs: [1, 2] } } }
 *  new settings: { table: { columns: { feedback: [6], subs: [1, 2] } } }
 */
export const setUserSettings = (newState) => {
  const origState = Object.assign({}, store.getState().user.settings);
  _combineObjects(newState, origState);

  return setUserData("settings", JSON.stringify(origState));
};

const _isObject = (variable) => {
  return variable !== null && typeof variable === "object";
};

const _combineObjects = (newObj, origObj) => {
  for (const [key, value] of Object.entries(newObj)) {
    if (
      // Do not access Object.prototype method 'hasOwnProperty' from target object.
      // https://stackoverflow.com/a/51148270 - Object.prototype.hasOwnProperty.call(origObj, key)
      // eslint-disable-next-line no-prototype-builtins
      origObj.hasOwnProperty(key) &&
      _isObject(value) &&
      _isObject(origObj[key])
    ) {
      _combineObjects(value, origObj[key]);
    } else {
      origObj[key] = value;
    }
  }
};

/*
 * Kayak has a separate user database for storing user specific information.
 * The user database's API allows saving miscellaneous key-value data pairs
 * to the database. The following functions getUserData and setUserData are
 * for utilizing this functionality.
 */

// Retrieve user's data from the database
export const getUserData = (key) => {
  const url = apiUrl(`users/data/${key}`, false);

  return fetchWithRetry("getJson", [url])
    .then((data) => {
      return data.value;
    })
    .catch((error) => {
      // Data for the key has not been set
      if (error.status === 404) {
        return null;
      } else {
        // Unexpected error so just throw it back
        let err = new Error();
        err = { ...error };
        throw err;
      }
    });
};

// Save user's data to the database
export const setUserData = (key, value) => {
  const url = apiUrl(`users/data/${key}`, false);
  const body = { value };

  return Fetcher.post(url, body);
};

/**
 * Check if user has access to certain action in Kayak
 * @param {string} accessRight
 */
export const userHasAccess = (accessRight) => {
  const DENIED_MODULE_NAMES = store.getState().user.DENIED_MODULE_NAMES;
  const result = DENIED_MODULE_NAMES.has(accessRight);

  // Return the opposite of result for slightly easier understanding
  return !result;
};

// Build 4.1.2.0(ERP #12346)
export const checkUserValidToPublisher = (userid, publisher) => {
  const urlStr = customUrl(`users/${(userid || "").toUpperCase()}/valid`, publisher);

  return Fetcher.getJson(urlStr)
    .then((response) => {
      return response;
    })
    .catch(() => {
      return false;
    });
};
